/**
 * NOTE: Please see index.ts for why these types are needed.
 */

import type {
  AirportRegion,
  Flights,
  Route,
  ShopFilter,
  TripDetailsV2,
} from "@b2bportal/air-shopping-api";
import type {
  CodeContext,
  AvailabilityResponse,
  ShopRequest,
  ShopResponse,
} from "@b2bportal/ground-api";
import type {
  DateRange,
  GuestsSelection,
  Initial,
  InitialSearch,
  LodgingSelection,
  More,
  Platform,
  RoomsSelection,
  ShopResponseAvailable,
} from "@b2bportal/lodging-api";
import type { PackageAvailabilityRequestEnum } from "@b2bportal/packages-api";
import type { Prices } from "@b2bportal/purchase-api";
import type {
  ChosenCar,
  ChosenLodging,
  ChosenFlight,
  ChosenProductsResponse,
} from "./benchmark";

export enum BundledProduct {
  Lodging = "LodgingProduct",
  Car = "CarProduct",
  Flight = "FlightProduct",
}

export interface PackageTotalPrice {
  totalPrice?: Prices;
  payNowTotal?: Prices;
  payLaterTotal?: Prices;
  tenantSpecific?: Prices;
}

export interface DateAdjustment {
  date: string;
  originRegion: AirportRegion;
}

export interface DynamicFields {
  adjustStartDateUsingLanding?: DateAdjustment;
  adjustEndDateUsingTakeoff?: DateAdjustment;
}

export interface LodgingAvailabilityRequestInitial extends InitialSearch {
  dynamicFields?: DynamicFields;
}

export interface LodgingAvailabilityRequest {
  dynamicFields?: DynamicFields;
  LodgingAvailabilityRequest: PackageAvailabilityRequestEnum;
  lodgingSelection: LodgingSelection;
  stayDates: DateRange;
  guests: GuestsSelection;
  rooms: RoomsSelection;
  platform: Platform;
}

export interface CarAvailabilityRequest {
  dynamicFields?: DynamicFields;
  pickupSelection: CodeContext;
  dropOffSelection: CodeContext;
  driverAge: number;
  pickupDateTime: string;
  dropoffDateTime: string;
}

export interface FlightShopRequest {
  route: Route;
  departureDate: string;
  returnDate?: string;
  passengers: { [key: string]: number };
  tripFilter: ShopFilter;
  fareClass: Array<string>;
  searchFixedPointsFares?: boolean;
}

export interface LodgingProductSearch {
  lodgingAvailabilityRequest: LodgingAvailabilityRequest;
}

export interface CarProductSearch {
  carAvailabilityRequest: CarAvailabilityRequest;
}

export interface FlightProductSearch {
  flightShopRequest: FlightShopRequest;
}

export interface ChosenProductSearch {
  chosenProduct: ChosenFlight;
}

export interface Requisition {
  products: (
    | LodgingProductSearch
    | CarProductSearch
    | FlightProductSearch
    | ChosenProductSearch
  )[];
}

export interface PackageBundleRequest {
  requisition: Requisition;
}

export enum BundleResponseEnum {
  FlightShopResponse = "FlightShopResponse",
  FlightTripDetailsResponse = "FlightTripDetailsResponse",
  LodgingAvailability = "LodgingAvailabilityResponse",
  LodgingShopResponse = "LodgingShopResponse",
  CarAvailabilityResponse = "CarAvailabilityResponse",
  CarShopResponse = "CarShopResponse",
}

export interface BundledPackageState {
  extraFields: { [key: string]: string };
  price: {
    incrementalChange: Prices;
    totalPrice: Prices;
    strikethroughPrice?: Prices;
    packageSavings?: Prices;
    tenantSpecific?: Prices;
  };
  token: { value: string };
}

export type BundledPackagePrice = BundledPackageState["price"];

export interface BundledPackageStateToLodgingId {
  [lodgingId: string]: BundledPackageState;
}

export interface BundledPackageStateToRateId {
  [rateId: string]: BundledPackageState;
}

export interface BundledPackageStateToFareId {
  [fareId: string]: BundledPackageState;
}

export interface BundledPackageStateToVehicleId {
  [vehicleId: string]: BundledPackageState;
}

export interface InitialLodgingAvailabilityResponse
  extends ChosenProductsResponse {
  BundleResponse: BundleResponseEnum.LodgingAvailability;
  lodgingAvailability: {
    packageStateByLodgingId: BundledPackageStateToLodgingId;
    payload: {
      availability: Initial;
    };
  };
}

export interface PackagesCustomizeLodgingAvailabilityRequest {
  customize: {
    bundledPackageStateToken: { value: string };
    lodgingAvailabilityRequest: LodgingAvailabilityRequest;
  };
}

export interface PackagesCustomizeLodgingAvailabilityMoreRequest {
  customize: {
    bundledPackageStateToken: { value: string };
    lodgingAvailabilityRequest: {
      moreToken: string;
      LodgingAvailabilityRequest: "FollowUpRequest";
    };
  };
}

export interface PackagesCustomizeLodgingAvailabilityMoreResponse {
  BundleResponse: BundleResponseEnum.LodgingAvailability;
  lodgingAvailability: {
    packageStateByLodgingId: BundledPackageStateToLodgingId;
    payload: {
      Payload: "FollowUpResponse";
      availability: More;
    };
  };
}

export interface PackagesCustomizeLodgingShopRequest {
  customize: {
    bundledPackageStateToken: { value: string };
    lodgingShopRequest: {
      opaqueShopRequest: string;
      platform: Platform;
    };
  };
}

export interface PackagesCustomizeLodgingShopResponse {
  BundleResponse: BundleResponseEnum.LodgingShopResponse;
  lodgingShop: {
    packageStateByRateId: BundledPackageStateToRateId;
    payload: {
      Payload: "ShopAvailable" | "ShopFailure" | "ShopUnavailable";
      available: ShopResponseAvailable;
    };
  };
}

export interface PackagesCustomizeFlightsShopRequest {
  customize: {
    bundledPackageStateToken: { value: string };
    flightShopRequest: FlightShopRequest;
  };
}

export interface PackagesCustomizeFlightsShopResponse
  extends ChosenProductsResponse {
  BundleResponse: BundleResponseEnum.FlightShopResponse;
  flightShop: {
    packageStateByFareId: BundledPackageStateToFareId;
    payload: {
      Payload:
        | "FlightShopSuccess"
        | "FlightShopFailure"
        | "FlightShopUnavailable";
      summarizeResponse: {
        flights: Flights;
      };
    };
  };
}

export interface PackagesCustomizeTripDetailsRequest {
  customize: {
    bundledPackageStateToken: { value: string };
    flightTripDetailsRequest: {
      tripId: string;
    };
  };
}

export interface PackagesCustomizeTripDetailsResponse {
  BundleResponse: BundleResponseEnum.FlightTripDetailsResponse;
  flightTripDetails: {
    packageStateByFareId: BundledPackageStateToFareId;
    result: TripDetailsV2;
  };
}

export interface PackagesCustomizeCarsAvailabilityRequest {
  customize: {
    bundledPackageStateToken: { value: string };
    carAvailabilityRequest: CarAvailabilityRequest;
  };
}

export interface PackagesCustomizeCarsAvailabilityResponse {
  BundleResponse: BundleResponseEnum.CarAvailabilityResponse;
  carAvailability: {
    packageStateByVehicleId: BundledPackageStateToVehicleId;
    payload: AvailabilityResponse;
  };
}

export interface PackagesCustomizeCarsShopRequest {
  customize: {
    bundledPackageStateToken: { value: string };
    carShopRequest: ShopRequest;
  };
}

export interface PackagesCustomizeCarsShopResponse {
  BundleResponse: BundleResponseEnum.CarShopResponse;
  carShop: {
    bundledPackageState: BundledPackageState;
    shopResponse: ShopResponse;
  };
}

export interface PackagesLodgingCompleteBundle {
  lodgingId: string;
  roomId: string;
  opaquePriceRequest: string;
  BundledProduct: BundledProduct.Lodging;
}

export interface PackagesCarCompleteBundle {
  vehicleId: { value: string };
  BundledProduct: BundledProduct.Car;
}

export interface PackagesFlightCompleteBundle {
  tripId: string;
  fareId: string;
  BundledProduct: BundledProduct.Flight;
}

export interface PackagesCompleteRequest {
  bundledPackageStateToken: { value: string };
}

export interface PackagesCompleteResponse {
  totalPrice: PackageTotalPrice;
  chosenProducts: (ChosenFlight | ChosenLodging | ChosenCar)[];
  bundledProducts: (
    | PackagesLodgingCompleteBundle
    | PackagesCarCompleteBundle
    | PackagesFlightCompleteBundle
  )[];
}
